import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logAdsVerbose } from '../../config/ads';
import { http } from '../../services/http';

/** Log once per page load so quiet sites are not spammed. */
let loggedManagedAdsOff = false;

export const fetchSitePublicSettings = createAsyncThunk(
  'sitePublic/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const res = await http.get('/api/v1/public/site-settings');
      const payload = res.data?.data ?? res.data ?? {};
      return {
        adsStaticEnabled: Boolean(payload.adsStaticEnabled),
        adsManagedEnabled: Boolean(payload.adsManagedEnabled),
        siteKey: String(payload.siteKey || ''),
      };
    } catch (err) {
      const status = err.response?.status;
      const body = err.response?.data;
      const msg =
        (typeof body === 'object' && body?.message) || body?.error || err.message || 'Failed to load site-settings';
      return rejectWithValue({ message: String(msg), status: status || null, path: '/api/v1/public/site-settings' });
    }
  },
);

const sitePublicSlice = createSlice({
  name: 'sitePublic',
  initialState: {
    loaded: false,
    loading: false,
    error: null,
    adsStaticEnabled: false,
    adsManagedEnabled: false,
    siteKey: '',
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSitePublicSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSitePublicSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.adsStaticEnabled = action.payload.adsStaticEnabled;
        state.adsManagedEnabled = action.payload.adsManagedEnabled;
        state.siteKey = action.payload.siteKey;
        if (logAdsVerbose) {
          console.log('[simimovies/ads] site-settings payload', action.payload);
        }
        if (!action.payload.adsManagedEnabled && !loggedManagedAdsOff) {
          loggedManagedAdsOff = true;
          console.info(
            '[simimovies/ads] Managed ads are OFF (site-settings: adsManagedEnabled=false). ' +
              'Turn on “Managed ads” for this Website in admin, or Website.key / isActive may not match the app (REACT_APP_SITE_KEY + X-Site).',
          );
        }
      })
      .addCase(fetchSitePublicSettings.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true;
        const payload = action.payload;
        const detail =
          payload && typeof payload === 'object'
            ? `${payload.path || 'site-settings'}${payload.status ? ` HTTP ${payload.status}` : ''}: ${payload.message || ''}`
            : action.error?.message || 'Failed to load site settings';
        state.error = detail;
        state.adsStaticEnabled = false;
        state.adsManagedEnabled = false;
        console.warn('[simimovies/ads] site-settings failed — no ad flags loaded.', detail);
        if (logAdsVerbose) {
          console.warn('[simimovies/ads] site-settings rejected (full)', action);
        }
      });
  },
});

export default sitePublicSlice.reducer;

export function selectSitePublicLoaded(state) {
  return state.sitePublic.loaded;
}

export function selectAdsStaticEnabled(state) {
  return state.sitePublic.adsStaticEnabled;
}

export function selectAdsManagedEnabled(state) {
  return state.sitePublic.adsManagedEnabled;
}

export function selectAdSurfaceEnabled(state) {
  return state.sitePublic.adsStaticEnabled || state.sitePublic.adsManagedEnabled;
}
